<template lang="pug">	
	.maps--statistics-info--layout-item.full-width
		.flex-col(v-if="isArray")
			.layout-group.space(:class="[item.type === 'column' ? 'flex-col align-end' : 'flex-row justify-end']")
				statistics-info-layout-item.flex-1(v-for="subItem in item.items", :item="subItem", :config="config", :statistics="statistics")
		.layout-item(v-else)
			.content.flex-row.space-large.wrap.full-width.justify-end
				.flex-col.text-end(v-if="item.type === 'field'")
					.font-small.upper.grey {{item.caption}}
					a.font-big(@click="onValueClick") {{formatter.format(total || 0)}}			
				.statistics-item.flex-1(v-if="item.type === 'group'", :style="{ 'flex-basis': item.type || item.width ? `${item.width || 300}px` : '160px' }")
					template(v-if="item.widget === 'bars'")
						.chart-item.flex-col.flex-1.space
							.font-small.upper.grey.text-end {{item.caption}}
							DxChart.flex-1(ref="chart", :data-source="totalData", :size="{ height: 175, width: '100%' }", :palette="palette", @point-click="onPointClick")
								DxSeries(argument-field="key", value-field="value", type="bar")
								DxLegend(:visible="false")
								DxCommonSeriesSettings(type="bar")
								DxTooltip(:enabled="true", :zIndex="100000", :color="subColor", :customizeTooltip="(e) => ({ text: `${item.caption}: ${e.argument}\n${formatter.format(e.value)}` })")
								DxValueAxis(:visible="false", :color="subColor", :grid="{ color: gridColor }", :tick="{ color: '#00000000' }", :label="{ font: { color: accentColor }}")
								DxArgumentAxis(:visible="true", :color="subColor", :grid="{ color: gridColor }", :label="{ font: { color: accentColor }, format: { type: 'thousands' } }", :tick="{ color: accentColor }")
					template(v-if="item.widget === 'pie'")
						.chart-item.flex-col.flex-1.space
							.font-small.upper.grey.text-end {{item.caption}}
							DxPieChart(
								ref="chart",
								:data-source="totalData",
								resolve-label-overlapping="shift",
								type="doughnut",
								:size="{ height: 180, width: '100%' }", :rotated="true",
								:series="pieChartSerieConfig",
								:legend="{ visible: true }",
								:inner-radius="0.65",
								:palette="palette",
								center-template="centerTemplate",
								@point-click="onPointClick"
							)
								DxTooltip(:enabled="true", :zIndex="100000", :color="subColor", :format="format", :customizeTooltip="(e) => ({ text: `${e.valueText}\n${e.argument}` })")
								template(v-slot:centerTemplate="center")
									svg
										text(text-anchor="middle"
											x="100"
											y="120"
										)
											tspan(:style="{fontSize: 50, fontName: 'Roboto', fontWeight: 100, fill: accentColor, letterSpacing: '-4px' }", x="100") {{maxPercent}}%
											tspan(:style="{fontSize: 20, fontName: 'Roboto', fontWeight: 100, fill: accentColor }", x="100", dy="20px") {{maxDataItem && maxDataItem.key}}
		Loader(v-if="isLoading", text="", :fullscreen="true")
</template>

<script>
	import _ from 'lodash';
	import Loader from 'app-core/Loader.vue';
	import { DxChart, DxSeries, DxLegend, DxSize, DxCommonSeriesSettings, DxTooltip, DxValueAxis, DxArgumentAxis, DxGrid, DxLabel, DxVisualRange, DxWholeRange, DxSeriesTemplate, DxFormat } from 'devextreme-vue/chart';
	import DxPieChart from 'devextreme-vue/pie-chart';

	export default {
		translationCategory: 'maps.ZipsMapLayoutItem',
		name: 'maps--zips-map--layout-item',
		components: { Loader, DxChart, DxSeries, DxLegend, DxSize, DxCommonSeriesSettings, DxTooltip, DxValueAxis, DxArgumentAxis, DxGrid, 
			DxLabel, DxVisualRange, DxWholeRange, DxSeriesTemplate, DxFormat, DxPieChart },
		props: ['statistics', 'config', 'item'],
		data() {
			const formatter = new Intl.NumberFormat('en-US', {
				style: 'decimal'
			});
			return {
				isLoading: 0,
				formatter,
				autoStyleField: null,
				format: 'thousands'
			};
		},
		computed: {
			isArray() {
				return this.item.items?.length && this.item.type !== 'group';
			},
			totals() {
				return this.calculateTotals();
			},
			total() {
				return this.getFieldTotal();
			},
			totalData() {
				return this.getTotalData();
			},
			pieChartSerieConfig() {
				return {
					argumentField: 'key',
					valueField: 'value',
					label: {
						backgroundColor: 'none',
						format: 'fixedPoint',
						connector: {
							visible: true
						},
						customizeText: ({ argumentText, valueText, percentText }) => `${argumentText}`
					}
				};
			},
			palette() {
				return this.config.palette || this.item.palette || ['#333', '#444', '#555', '#666', '#777'];
			},
			accentColor() {
				return this.item.accentColor || this.palette[0];
			},
			subColor() {
				return this.item.subColor || this.palette[1];
			},
			gridColor() {
				return this.item.gridColor || '#ddd';
			},
			maxDataItem() {
				return _.maxBy(this.totalData, (v) => v.value);
			},
			maxPercent() {
				return parseInt(this.maxDataItem?.value / _.sumBy(this.totalData, v => v.value) * 100) || 0;
			}
		},
		watch: {
			
		},
		mounted() {
			if (__DEBUG__)
				window[`statisticsInfo_${this.item.fieldName}`] = this;
			
			this.$eventsBus.$on('zips-map-clear-selection', this.onClearSelection);
		},
		destroyed() {
			this.$eventsBus.$off('zips-map-clear-selection', this.onClearSelection);
		},
		methods: {
			onClearSelection(callback) {
				if (this.$refs?.chart?.instance?.clearSelection) {
					this.$refs?.chart?.instance?.clearSelection();
				}

				if (callback) callback();
			},
			onPointClick(e) {
				if (__DEBUG__)
					console.debug('Autostyle', { e });
				this.autoStyleFieldWidgetChanged(e.target.data);
				e.target.select();
			},
			onValueClick() {
				this.autoStyleFieldWidgetChanged(this.item);
			},
			autoStyleFieldWidgetChanged(field) {
				this.$eventsBus.$emit('zips-map-clear-selection');

				if (__DEBUG__)
					console.debug('Autostyle', { field });

				this.$eventsBus.$emit('zips-map-auto-style-field-changed', field);
			},
			getFieldTotal() {
				let field = this.item;
				switch(field.calcType) {
					case 'avg':
						return parseInt(_.sumBy(this.statistics, (v) => Math.max(v[field.fieldName], 0)) / this.statistics.length * 10) / 10;
					case 'median':
						return this.median(this.statistics.map(f => Math.max(f[field.fieldName], 0)));
					default:
						return _.sumBy(this.statistics, (v) => Math.max(v[field.fieldName], 0));
				}
			},
			getTotalData() {
				let summarize = (field) => {
					switch(field.calcType) {
						case 'avg':
							return parseInt(_.sumBy(this.statistics, (v) => Math.max(v[field.fieldName], 0)) / this.statistics.length * 10) / 10;
						case 'median':
							return this.median(this.statistics.map(f => Math.max(f[field.fieldName], 0)));
						default:
							return _.sumBy(this.statistics, (v) => Math.max(v[field.fieldName], 0));
					}
				}

				let summary = this.item.items?.map(item => ({
					key: item.caption,
					value: summarize(item),
					fieldName: item.fieldName
				})) || [];

				this.$eventsBus.$emit('zips-map--group-statistics', {
					group: this.item,
					summary
				});

				return summary;
			},
			median(values) {
				if (values.length === 0) {
					return 0;
				}

				values = [...values].sort((a, b) => a - b);

				const half = Math.floor(values.length / 2);

				return (values.length % 2
					? values[half]
					: (values[half - 1] + values[half]) / 2
				);
			}
		}
	};
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.maps--statistics-info--layout-item {
		.header {
			font-weight: 100;
			font-size: 16px !important;
		}

		.layout-item {
			padding: 4px;

			.content {
				padding: 10px;
				background: #fff;
			}
		}
	}
</style>
